import { graphql } from "gatsby";
import { RichTextType, SimplifyGatsbyQuery, useCartContext, ILanguage, IParkTheme } from "@parksandresorts/core";
import styles from "./my-page-block.module.less";
import { GlobalContextConsumer } from "components/stores/global-context";

import MyParks from "@parksandresorts/myparks";
import { ICustomTexts } from "@parksandresorts/myparks/dist/helpers/host";
import { useMemo } from "react";

const URL = process.env.GATSBY_MY_PARKS_URL || "https://prs-cdp-dev-myparks-webapp.azurewebsites.net/api";
const COMPANY = process.env.GATSBY_MY_PARKS_COMPANY || "TestSSL";

const calendarLoaderIcons = {
  firstImage: "/loader-images/wave.svg",
  secondImage: "/loader-images/glass.svg",
  thirdImage: "/loader-images/beach-umbrella.svg",
};

const serviceLayerApiBaseUrl =
  process.env.GATSBY_PRS_API_URL || "https://prs-cdp-dev-webapiproxy.azurewebsites.net/api/ssl/";

type Props = SimplifyGatsbyQuery<Queries.MyPageBlockFragment> & {
  isShowingTopBanner: boolean; //This comes from site-blocks
  node_locale: ILanguage;
  theme: IParkTheme;
  bookingCompletedRichText: RichTextType;
};

const MyPageBlock = (props: Props) => {
  const cartContext = useCartContext();

  const labelsOverride = useMemo(() => {
    const tempLabelsOverride = new Map();

    const labelsOverridesList = props.labelOverrides?.list ?? [];
    if (labelsOverridesList) {
      labelsOverridesList.forEach((item) => {
        if (item && item.key && item.value) {
          tempLabelsOverride.set(item.key, item.value);
        }
      });
    }

    return tempLabelsOverride;
  }, [props.labelOverrides]);

  const customTexts: ICustomTexts = {
    GENERAL_BOOKING_CONDITIONS: props.generalBookingConditions,
    BOOKING_COMPLETED_RICH_TEXT: props.bookingCompletedRichText,
    pageIntroParagraph: props.discountPageIntroParagraph,
    activeDiscountsParagraph: props.discountPageActiveDiscountsParagraph,
    upsellParagraph: props.discountPageUpsellParagraph,
    usedDiscountsParagraph: props.discountPageUsedParagraph,
    nonActiveDiscountsParagraph: props.discountPageNonActiveParagraph,
    entryTicketCardParagraph: props.discountPageEntryTicketCardParagraph,
    seasonPassCardParagraph: props.discountPageSeasonPassCardParagraph,
  };

  return (
    <GlobalContextConsumer>
      {(globalContext) => (
        <section
          className={[
            styles.myPageBlock,
            globalContext.isTopNavigationHidden ? styles.noPaddingTop : "",
            props.isShowingTopBanner ? styles.extraPaddingTop : null,
          ].join(" ")}
          data-contentful-id={props.contentful_id}
        >
          <MyParks
            language={props.node_locale || "sv"}
            parkTheme="sommarland"
            myParksApiUrl={URL}
            myParksCompanyId={COMPANY}
            debug={false}
            parkLogo="/images/my-pages/sommarland-logo.svg"
            calendarLoaderIcons={calendarLoaderIcons}
            globalContext={globalContext}
            generalBookingConditionsLink={
              props.generalBookingConditionsLink?.internalLink?.slug ?? props.generalBookingConditionsLink?.externalUrl
            }
            serviceLayerApiBaseUrl={serviceLayerApiBaseUrl}
            discountPageImages={props.discountsParagraphParkLogos}
            labelsOverride={labelsOverride}
            featureConfig={{
              enableParking: true,
              enableSharingProducts: false,
              enableDiscountsTickets: props.enableDiscountsTickets ?? false,
              enableDiscountsSeasonPasses: props.enableDiscountsSeasonPasses ?? false,
              enableWallet: true,
            }}
            customTexts={customTexts}
            cartContext={cartContext}
            topMenuParkLogo={props.topMenuParkLogo?.file?.url ?? undefined}
            startPageUpsellCards={props.startPageUpsellCards ?? []}
            seasonPassPageUpsellCards={props.seasonPassPageUpsellCards ?? []}
            handleTicketPageUpsellCards={props.handleTicketPageUpsellCards ?? []}
            orderHistoryPageUpsellCards={props.orderHistoryPageUpsellCards ?? []}
            personDetailsPageUpsellCards={props.personDetailsPageUpsellCards ?? []}
            bookingCompletedPageUpsellCards={props.bookingCompletedPageUpsellCards ?? []}
            discountPageUpsellCards={props.discountPageUpsellCards ?? []}
            parkingRegisteredCarsAnswer={props.parkingRegisteredCarsAnswer}
            parkingAvailableRegistrationsAnswer={props.parkingAvailableRegistrationsAnswer}
            parkingQuestionsAndAnswers={
              props.parkingQuestionsAndAnswers
                ? props.parkingQuestionsAndAnswers
                    .filter((textBlock) => textBlock.title && textBlock.richText)
                    .map((textBlock) => ({
                      question: textBlock.title,
                      answer: textBlock.richText,
                    }))
                : undefined
            }
          ></MyParks>
        </section>
      )}
    </GlobalContextConsumer>
  );
};

export default MyPageBlock;

export const fragmentUpsellCard = graphql`
  fragment UpsellCard on ContentfulGeneralContentBlock {
    title
    shortDescriptionInList
    imageInList {
      file {
        url
      }
    }
    link {
      title
      internalLink {
        slug
      }
      externalUrl
      openInNewTab
    }
  }
`;

export const fragmentMyPageBlock = graphql`
  fragment MyPageBlock on ContentfulMyPageBlock {
    contentful_id
    id
    node_locale
    labelOverrides {
      list {
        key
        value
      }
    }
    theme
    generalParkLogo {
      file {
        url
      }
    }
    topMenuParkLogo {
      file {
        url
      }
    }
    generalBookingConditionsLink {
      ...Link
    }
    generalBookingConditions {
      raw
    }
    bookingCompletedRichText {
      raw
      references {
        __typename
        ...Link
        ...Asset
      }
    }
    startPageUpsellCards {
      ...UpsellCard
    }
    seasonPassPageUpsellCards {
      ...UpsellCard
    }
    handleTicketPageUpsellCards {
      ...UpsellCard
    }
    orderHistoryPageUpsellCards {
      ...UpsellCard
    }
    personDetailsPageUpsellCards {
      ...UpsellCard
    }
    bookingCompletedPageUpsellCards {
      ...UpsellCard
    }
    enableDiscountsTickets
    enableDiscountsSeasonPasses
    discountsParagraphParkLogos {
      gatsbyImageData(height: 60)
    }
    discountPageUpsellCards {
      ...UpsellCard
    }
    discountPageIntroParagraph {
      raw
    }
    discountPageActiveDiscountsParagraph {
      raw
    }
    discountPageUpsellParagraph {
      raw
    }
    discountPageUsedParagraph {
      raw
    }
    discountPageNonActiveParagraph {
      raw
    }
    discountPageEntryTicketCardParagraph {
      raw
    }
    discountPageSeasonPassCardParagraph {
      raw
    }
    parkingRegisteredCarsAnswer {
      raw
    }
    parkingAvailableRegistrationsAnswer {
      raw
    }
    parkingQuestionsAndAnswers {
      ...TextBlock
    }
  }
`;
